<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { api, type ServiceChargeItem, type ServicePriceItem, type ServicesDocument } from "./api";

const props = defineProps<{ siteId: string }>();

const document = ref<ServicesDocument | null>(null);
const selectedIndex = ref(0);
const canEdit = ref(false);
const loading = ref(true);
const saving = ref(false);
const error = ref("");
const notice = ref("");
const service = computed(() => document.value?.items[selectedIndex.value] ?? null);

function blankService(): ServicePriceItem {
  return {
    name: "New service",
    shortLabel: "",
    description: "",
    price: "",
    priceSuffix: "per visit",
    durationMinutes: null,
    features: [],
    isFeatured: false,
    isPublished: true,
  };
}

function addService() {
  if (!document.value) return;
  document.value.items.push(blankService());
  selectedIndex.value = document.value.items.length - 1;
}

function removeService() {
  if (!document.value || !service.value) return;
  document.value.items.splice(selectedIndex.value, 1);
  selectedIndex.value = Math.max(0, selectedIndex.value - 1);
}

function moveService(direction: -1 | 1) {
  if (!document.value) return;
  const target = selectedIndex.value + direction;
  if (target < 0 || target >= document.value.items.length) return;
  const [item] = document.value.items.splice(selectedIndex.value, 1);
  if (!item) return;
  document.value.items.splice(target, 0, item);
  selectedIndex.value = target;
}

function addFeature() {
  service.value?.features.push("");
}

function blankCharge(): ServiceChargeItem {
  return {
    name: "New charge",
    description: "",
    kind: "fixed",
    amount: "",
    suffix: "",
    isPublished: true,
  };
}

function addCharge() {
  document.value?.charges.push(blankCharge());
}

function removeCharge(index: number) {
  document.value?.charges.splice(index, 1);
}

function moveCharge(index: number, direction: -1 | 1) {
  if (!document.value) return;
  const target = index + direction;
  if (target < 0 || target >= document.value.charges.length) return;
  const [charge] = document.value.charges.splice(index, 1);
  if (!charge) return;
  document.value.charges.splice(target, 0, charge);
}

async function save() {
  if (!document.value || !canEdit.value) return;
  saving.value = true;
  error.value = "";
  notice.value = "";
  try {
    document.value = (await api.updateServices(props.siteId, document.value)).services;
    notice.value = "Services and prices saved.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to save services";
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  try {
    const response = await api.services(props.siteId);
    document.value = response.services;
    canEdit.value = response.canEdit;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load services";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section class="services-card">
    <div class="module-heading">
      <div>
        <p class="eyebrow">Service price sheet</p>
        <h2>Services &amp; prices</h2>
        <p>Explain what customers receive and keep prices in one reusable place.</p>
      </div>
      <button type="button" :disabled="saving || !canEdit" @click="save">
        {{ saving ? "Saving…" : "Save services" }}
      </button>
    </div>

    <p v-if="error" class="message error" role="alert">{{ error }}</p>
    <p v-if="notice" class="message success" role="status">{{ notice }}</p>
    <div v-if="loading" class="loading">Loading services…</div>

    <template v-else-if="document">
      <div class="catalogue-fields">
        <label>Page heading<input v-model="document.title" :disabled="!canEdit" maxlength="160"></label>
        <label class="wide">Introduction<textarea v-model="document.introduction" :disabled="!canEdit" rows="3" maxlength="1000" /></label>
        <label class="wide">Price note<textarea v-model="document.footnote" :disabled="!canEdit" rows="2" maxlength="1000" placeholder="For example: Bank holidays may cost extra." /></label>
      </div>

      <nav class="service-tabs" aria-label="Services">
        <button
          v-for="(item, index) in document.items"
          :key="item.id || index"
          type="button"
          :class="{ active: selectedIndex === index }"
          @click="selectedIndex = index"
        >
          {{ item.name || `Service ${index + 1}` }}
          <small>{{ item.isPublished ? "Published" : "Hidden" }}</small>
        </button>
        <button class="add-tab" type="button" :disabled="!canEdit" @click="addService">+ Add service</button>
      </nav>

      <article v-if="service" class="service-editor">
        <div class="service-toolbar">
          <strong>{{ service.name || "Untitled service" }}</strong>
          <div>
            <button type="button" :disabled="!canEdit || selectedIndex === 0" @click="moveService(-1)">Move left</button>
            <button type="button" :disabled="!canEdit || selectedIndex === document.items.length - 1" @click="moveService(1)">Move right</button>
            <button class="remove" type="button" :disabled="!canEdit" @click="removeService">Remove</button>
          </div>
        </div>

        <div class="fields">
          <label>Service name<input v-model="service.name" :disabled="!canEdit" maxlength="160"></label>
          <label>Short label<input v-model="service.shortLabel" :disabled="!canEdit" maxlength="120" placeholder="Daily drop-in"></label>
          <label class="wide">Description<textarea v-model="service.description" :disabled="!canEdit" rows="4" maxlength="1000" /></label>
          <label>Price (£)<input v-model="service.price" :disabled="!canEdit" type="number" min="0" step="0.01" placeholder="15.00"></label>
          <label>Shown after price<input v-model="service.priceSuffix" :disabled="!canEdit" maxlength="80" placeholder="per visit"></label>
          <label>Duration in minutes<input v-model.number="service.durationMinutes" :disabled="!canEdit" type="number" min="1" max="1440" placeholder="30"></label>
          <div class="checks"><label><input v-model="service.isPublished" :disabled="!canEdit" type="checkbox"> Published</label><label><input v-model="service.isFeatured" :disabled="!canEdit" type="checkbox"> Featured</label></div>
        </div>

        <section class="features">
          <div class="section-heading"><div><h3>What’s included</h3><p>Short points shown on the price card.</p></div><button type="button" :disabled="!canEdit" @click="addFeature">Add item</button></div>
          <div v-for="(_feature, index) in service.features" :key="index" class="feature-row">
            <input v-model="service.features[index]" :disabled="!canEdit" maxlength="200">
            <button class="remove" type="button" :disabled="!canEdit" @click="service.features.splice(index, 1)">Remove</button>
          </div>
        </section>
      </article>

      <div v-else class="empty"><p>No services yet.</p><button type="button" :disabled="!canEdit" @click="addService">Add the first service</button></div>

      <section class="charges-editor">
        <div class="section-heading">
          <div>
            <h3>Additional charges</h3>
            <p>Add mileage, extra-cat fees, or seasonal percentage surcharges.</p>
          </div>
          <button type="button" :disabled="!canEdit" @click="addCharge">Add charge</button>
        </div>

        <article v-for="(charge, index) in document.charges" :key="charge.id || index" class="charge-editor">
          <div class="charge-toolbar">
            <strong>{{ charge.name || `Charge ${index + 1}` }}</strong>
            <div>
              <button type="button" :disabled="!canEdit || index === 0" @click="moveCharge(index, -1)">Up</button>
              <button type="button" :disabled="!canEdit || index === document.charges.length - 1" @click="moveCharge(index, 1)">Down</button>
              <button class="remove" type="button" :disabled="!canEdit" @click="removeCharge(index)">Remove</button>
            </div>
          </div>
          <div class="charge-fields">
            <label>Name<input v-model="charge.name" :disabled="!canEdit" maxlength="160" placeholder="Additional cat"></label>
            <label>Type
              <select v-model="charge.kind" :disabled="!canEdit">
                <option value="fixed">Fixed amount</option>
                <option value="percentage">Percentage surcharge</option>
              </select>
            </label>
            <label>{{ charge.kind === "percentage" ? "Percentage (%)" : "Amount (£)" }}<input v-model="charge.amount" :disabled="!canEdit" type="number" min="0" step="0.01" placeholder="3.00"></label>
            <label>Shown after amount<input v-model="charge.suffix" :disabled="!canEdit" maxlength="80" placeholder="per additional cat"></label>
            <label class="wide">Explanation<textarea v-model="charge.description" :disabled="!canEdit" rows="2" maxlength="500" placeholder="When this charge applies." /></label>
            <label class="published-check"><input v-model="charge.isPublished" :disabled="!canEdit" type="checkbox"> Published</label>
          </div>
        </article>
        <p v-if="!document.charges.length" class="empty-inline">No additional charges added.</p>
      </section>
    </template>
  </section>
</template>

<style scoped>
.services-card,.service-editor,.features,.charges-editor{display:grid;gap:1.2rem}.services-card{padding:1.5rem;border:1px solid #d9ddd8;border-radius:1rem;background:#fff}.module-heading,.service-toolbar,.section-heading,.charge-toolbar{display:flex;align-items:center;justify-content:space-between;gap:1rem}.module-heading h2,.section-heading h3{margin:0}.module-heading p:last-child,.section-heading p{margin:.35rem 0 0;color:#6d7972}.eyebrow{margin:0 0 .25rem;color:#6b7b72;font-size:.68rem;font-weight:800;letter-spacing:.1em;text-transform:uppercase}.catalogue-fields,.fields,.charge-fields{display:grid;grid-template-columns:1fr 1fr;gap:.85rem}.wide{grid-column:1/-1}label{display:grid;gap:.4rem}textarea{width:100%;padding:.75rem;resize:vertical;border:1px solid #cbd5ce;border-radius:.65rem;background:#fff;font:inherit}.service-tabs{display:flex;overflow-x:auto;gap:.35rem;padding:.35rem;border:1px solid #dce2dc;border-radius:.75rem;background:#f5f7f5}.service-tabs button{min-width:max-content;color:#456055;background:transparent}.service-tabs button.active{color:#fff;background:#285c47}.service-tabs small{display:block;margin-top:.15rem;font-size:.62rem;text-transform:uppercase}.service-tabs .add-tab{border:1px dashed #8ea096}.service-editor,.charge-editor{padding:1.2rem;border:1px solid #dce2dc;border-radius:.8rem;background:#fafbf9}.service-toolbar>div,.charge-toolbar>div,.checks{display:flex;gap:.5rem}.service-toolbar button,.charge-toolbar button,.section-heading button{padding:.55rem .7rem;color:#365045;background:#e8eeea}.remove{color:#91382f!important;background:#fae9e7!important}.checks{align-items:end}.checks label,.published-check{display:flex;align-items:center}.checks input,.published-check input{width:auto}.features,.charges-editor{padding-top:1rem;border-top:1px solid #dce2dc}.feature-row{display:grid;grid-template-columns:1fr auto;gap:.6rem}.empty-inline{margin:0;color:#6d7972}.message{margin:0;padding:.85rem 1rem;border-radius:.65rem}.message.error{color:#86251f;background:#fee9e7}.message.success{color:#295b40;background:#e2f1e7}.loading,.empty{padding:2rem;text-align:center}@media(max-width:700px){.module-heading,.service-toolbar,.section-heading,.charge-toolbar{align-items:stretch;flex-direction:column}.catalogue-fields,.fields,.charge-fields{grid-template-columns:1fr}.wide{grid-column:auto}.service-toolbar>div,.charge-toolbar>div,.checks{flex-wrap:wrap}.feature-row{grid-template-columns:1fr}}
</style>
